import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/solid';
import { Dropdown } from '@praetorian-chariot/ui';
import { useMemo, useState } from 'react';

import { Row } from '@/components/Row';
import {
  DeleteCustomersModal,
  UpdateCustomerTypeModal,
} from '@/sections/customerManagement/components/modals';
import {
  getCustomerDeleteOptions,
  getCustomerExportOptions,
} from '@/sections/customerManagement/config/actionOptions';
import { useCustomerActionHandlers } from '@/sections/customerManagement/hooks/actions';
import {
  CustomerData,
  CustomerModalType,
} from '@/sections/customerManagement/types';
import { cn } from '@/utils/classname';

interface CustomerActionsProps {
  /** Customers to perform actions on (filtered by selection) */
  customers: CustomerData[];

  /** Selected row IDs from table */
  selectedRows: string[];

  /** Callback to clear selection after action */
  onClearSelection: () => void;

  /** Additional CSS classes */
  className?: string;

  /** Hide the "Actions:" label */
  hideLabel?: boolean;

  /** Hide all action buttons */
  hideActions?: boolean;
}

/**
 * Customer Actions Toolbar
 *
 * Provides bulk action operations for customer management table.
 * Displays dropdown menus for management actions (update type, delete) and export.
 *
 * Follows the asset actions pattern with dropdown option builders and
 * modal state management.
 */
export function CustomerActions({
  customers,
  selectedRows,
  onClearSelection,
  className,
  hideLabel,
  hideActions,
}: CustomerActionsProps) {
  const handlers = useCustomerActionHandlers();
  const [activeModal, setActiveModal] = useState<CustomerModalType | null>(
    null
  );

  // Filter customers based on selection
  const selectedCustomers = useMemo(() => {
    if (selectedRows.length === 0) return [];
    return customers.filter((_, index) => selectedRows.includes(String(index)));
  }, [customers, selectedRows]);

  // Modal open handler
  const handleModalOpen = (
    modalType: CustomerModalType,
    _customers: CustomerData[]
  ) => {
    setActiveModal(modalType);
  };

  // Modal close handler with selection clearing
  const handleModalClose = () => {
    setActiveModal(null);
  };

  // Success handler for modal actions
  const handleSuccess = () => {
    setActiveModal(null);
    onClearSelection();
  };

  // Build dropdown options using option builders
  const manageOptions = getCustomerDeleteOptions({
    customers: selectedCustomers,
    handlers,
    onModalOpen: handleModalOpen,
  });

  const exportOptions = getCustomerExportOptions({
    customers: selectedCustomers,
    handlers,
  });

  return (
    <Row className={cn(className)}>
      <div
        className={cn('flex items-center gap-3', hideActions && 'invisible')}
      >
        {!hideLabel && <h2 className="text-sm font-bold">Actions:</h2>}

        <Dropdown
          title="Manage Options"
          size="xs"
          options={manageOptions}
          placeholder="Manage"
          styleType="rounded"
          border={false}
          className="[&_span:nth-child(3)]:hidden"
          icon={<EllipsisHorizontalCircleIcon className="size-4" />}
          fitWidth={true}
        />

        <Dropdown
          title="Export Options"
          size="xs"
          options={exportOptions}
          placeholder="Export"
          styleType="rounded"
          border={false}
          className="[&_span:nth-child(3)]:hidden"
          icon={<EllipsisHorizontalCircleIcon className="size-4" />}
          fitWidth={true}
        />
      </div>

      {/* Modals */}
      {activeModal === 'updateType' && (
        <UpdateCustomerTypeModal
          isOpen={true}
          onClose={handleModalClose}
          customers={selectedCustomers}
          source="table"
          onSuccess={handleSuccess}
        />
      )}

      {activeModal === 'delete' && (
        <DeleteCustomersModal
          isOpen={true}
          onClose={handleModalClose}
          customers={selectedCustomers}
          source="table"
          onSuccess={handleSuccess}
        />
      )}
    </Row>
  );
}
